/**
 * AnomalousBehaviors.js - Anomalous Aircraft Behavior System
 *
 * Anomalous aircraft have unique, hard-coded behaviors that override normal flight logic.
 * Each plane does something weird, impossible, or cursed. These are not bugs. They are features.
 *
 * Architecture:
 * - Single entry point: processAnomalousBehavior() called at lifecycle moments
 * - Returns modified flight data + side effects array - doesn't mutate directly
 * - Core systems call THIS function and apply returned changes
 * - Side effects processed by separate handler
 * - Errors caught and flights proceed normally - fail safe, not fail catastrophic
 */

import { debug } from '../lib/debug.js';

// =============================================================================
// ANOMALOUS BEHAVIORS DATA
// =============================================================================

export const ANOMALOUS_BEHAVIORS = {
  "agee-whells": {
    name: "Agee-Whells",
    behavior: "reverse_other_flights",
    description: "All other flights reverse course when this one dispatches.",
    config: {
      reverseRadius: "global", // All flights worldwide
      toastOnDispatch: "ANOMALY: Agee-Whells has reversed the course of all other flights.",
      modalOnDispatch: {
        title: "ANOMALY DETECTED",
        subtitle: "AGEE-WHELLS",
        tagline: "Reality bends around this aircraft.",
        message: "All other flights have reversed course. They're heading back where they came from."
      }
    }
  },

  "vtonly": {
    name: "VTOnly",
    behavior: "vertical_only",
    description: "Engines only point up. Flies straight up, straight down. Arrived.",
    config: {
      flightAnimation: "vertical",
      distanceTraveled: 0,
      revenueMultiplier: 1.0,
      toastOnComplete: "VTOnly flew up. Then down. Flight complete."
    }
  },

  "the-bermudan": {
    name: "The Bermudan",
    behavior: "bermuda_redirect",
    description: "Always goes to Bermuda. No matter what you set.",
    config: {
      forceDestination: "TXKF",
      toastOnDispatch: "Your flight has been... redirected.",
      revenueBasedOnActual: true
    }
  },

  "the-moonshiner": {
    name: "The Moonshiner",
    behavior: "night_smuggler",
    description: "Only flies after 22:00. High risk of fines.",
    config: {
      dispatchWindowStart: 22,
      dispatchWindowEnd: 4,
      blockDispatchMessage: "The Moonshiner only flies after dark.",
      fineChance: 0.65,
      fineAmount: 1000000,
      groundedDays: 14,
      toastOnFine: "BUSTED: $1,000,000 fine. Moonshiner grounded for 2 weeks.",
      toastOnSuccess: "The Moonshiner slipped through undetected."
    }
  },

  "spruce-moose": {
    name: "Spruce Moose",
    behavior: "bathtub_flight",
    description: "A tiny wooden model. Flies in your bathtub. Once.",
    config: {
      revenueCents: 1,
      flightDurationSeconds: 1,
      permanentGround: true, // After flying once, becomes museum piece forever
      toastOnComplete: "You played with it in your bathtub. It flew for 1 second then landed in the water. Flight a success? It now lives in your trophy case."
    }
  },

  "quantum-hopper": {
    name: "Quantum Hopper",
    behavior: "quantum_time",
    description: "May arrive before it departs. Time is a suggestion.",
    config: {
      arriveBeforeDepartureChance: 0.5, // 50% chance to "arrive yesterday"
      instantArrivalChance: 0.3, // 30% instant
      normalFlightChance: 0.2, // 20% normal
      maxJumps: 10,
      toastOnTimeTravel: "Quantum Hopper arrived 24 hours ago. Check your logs.",
      toastOnInstant: "Quantum Hopper arrived instantly. Space-time is upset.",
      toastOnNormal: "Quantum Hopper arrived normally. How boring.",
      toastOnJump: "Quantum jump {current}/{max} complete.",
      toastOnRetire: "Quantum decoherence. The probability wave has collapsed."
    }
  },

  "the-mobius": {
    name: "The Mobius",
    behavior: "return_to_origin",
    description: "Always returns to origin. Infinite loop. Can never truly arrive.",
    config: {
      alwaysReturnToOrigin: true,
      flightAnimation: "mobius_strip",
      toastOnDispatch: "The Mobius begins its journey... back to where it started.",
      toastOnComplete: "The Mobius has returned to its origin. As it always does. As it always will."
    }
  },

  "schrodinger-jet": {
    name: "Schrodinger Jet",
    behavior: "negative_passengers",
    description: "Carries negative passengers. Still profitable. Physics weeps.",
    config: {
      passengerCount: -1, // Negative one passenger
      revenuePositive: true, // Still makes money somehow
      quantumVisual: true, // Glitchy appearance on map
      toastOnDispatch: "Schrodinger Jet departs with -1 passengers. They are simultaneously there and not.",
      toastOnComplete: "Schrodinger Jet arrived. Passenger count: -1. Revenue: positive. Reality: broken."
    }
  },

  "negative-one": {
    name: "Negative One",
    behavior: "negative_stats",
    description: "All stats negative. Generates fuel. You pay passengers to fly.",
    config: {
      fuelConsumed: -100,
      passengerCapacity: -1,
      revenuePerFlight: -5000,
      toastOnComplete: "Negative One completed its flight. You paid someone $5,000 to be on it."
    }
  },

  "ghost-freight": {
    name: "Ghost Freight",
    behavior: "ghost_operation",
    description: "No crew. No fuel. No cargo. Still profitable.",
    config: {
      skipCrewRequirement: true,
      skipFuelRequirement: true,
      skipCargoLoading: true,
      ghostVisual: true, // Semi-transparent on map
      revenueMultiplier: 1.0, // Full revenue despite no real operation
      toastOnDispatch: "Ghost Freight phases through reality. No crew, no fuel, no cargo aboard.",
      toastOnComplete: "Ghost Freight completed its haunted route. Profit materialized from nothing."
    }
  },

  "the-perpetual": {
    name: "The Perpetual",
    behavior: "eternal_flight",
    description: "Circumnavigates the equator forever. Never lands. Launched 1987.",
    config: {
      neverLands: true,
      flightPath: "equator_loop",
      passiveIncomePerHour: 10000,
      // Equator waypoints for circumnavigation
      equatorWaypoints: ["SEQM", "DNMM", "VRMM", "WIII"], // Quito → Lagos → Malé → Jakarta → repeat
      toastOnDispatch: "The Perpetual ignores your route. It has begun circumnavigating the equator. Forever.",
      toastOnManualStop: "You forced The Perpetual to land. It had been airborne since 1987."
    }
  },

  "blackout": {
    name: "Blackout",
    behavior: "invisible_flight",
    description: "Disappears from radar and memory. Arrives without being tracked.",
    config: {
      invisibleOnMap: true, // Don't show on world map during flight
      skipFlightTracking: true, // No flight log entry
      ignoreWeather: true,
      ignoreTurbulence: true,
      toastOnDispatch: "Blackout has departed. You won't see it again until it lands.",
      toastOnComplete: "Blackout has arrived. You don't remember dispatching it."
    }
  },

  "revenue-engine": {
    name: "Revenue Engine",
    behavior: "passive_income",
    description: "Can't fly. Generates $10M/day just by existing.",
    config: {
      cannotFly: true, // Cannot be dispatched
      passiveIncomeCentsPerDay: 1000000000, // $10,000,000/day in cents
      blockDispatchMessage: "Revenue Engine doesn't fly. It just prints money.",
      toastOnDayEnd: "Revenue Engine generated $10,000,000 today. Money printer go brrr."
    }
  },

  "yesterday": {
    name: "Yesterday",
    behavior: "instant_arrival",
    description: "Arrives before it departs. You missed it.",
    config: {
      instantComplete: true,
      retireImmediately: true,
      toastOnDispatch: "The Yesterday took off. But you'd have to have been here yesterday to see it."
    }
  },

  "the-jinx": {
    name: "The Jinx",
    behavior: "causes_other_failures",
    description: "Every flight causes another to fail. The Jinx itself always completes.",
    config: {
      failOtherFlightOnDispatch: true, // Random other flight fails when this dispatches
      guaranteedOwnCompletion: true, // The Jinx itself always succeeds
      toastOnDispatch: "The Jinx has departed. Another flight will pay the price.",
      toastOnOtherFail: "JINXED: Flight {flightNumber} has failed due to The Jinx's curse.",
      toastOnComplete: "The Jinx arrives safely. It always does. The curse is for others."
    }
  },

  "infinite-loop": {
    name: "Infinite Loop",
    behavior: "figure_eight_forever",
    description: "All stats are 8. Flies figure-8s forever. Never lands.",
    config: {
      flightAnimation: "figure_eight",
      neverLands: true, // Flies forever until manually stopped
      passiveIncomePerHour: 8888, // Generates $88.88/hour while flying
      allStatsAre8: true, // Visual gag - all displayed stats show 8
      toastOnDispatch: "Infinite Loop begins its eternal figure-8. It will never land on its own.",
      toastOnManualStop: "Infinite Loop has been forcibly landed. break; statement executed."
    }
  },

  "the-fold": {
    name: "The Fold",
    behavior: "space_fold",
    description: "Folds space. Instant travel. $47M fuel cost per flight.",
    config: {
      instantTravel: true, // Instant arrival
      fuelCostCents: 4700000000, // $47,000,000 in cents
      toastOnDispatch: "Space folds around The Fold. $47,000,000 in exotic fuel consumed.",
      toastOnComplete: "The Fold has arrived. Instantly. At great cost."
    }
  },

  "echo-flight": {
    name: "Echo Flight",
    behavior: "duplicates_self",
    description: "Duplicates on each flight. Cannot unschedule. Fleet grows exponentially.",
    config: {
      duplicateOnComplete: true, // Creates a copy of itself in your fleet
      maxDuplicates: 10, // Caps at 10 total Echo Flights
      cannotUnschedule: true, // Once scheduled, it keeps flying
      autoRedispatch: true, // Immediately flies again
      toastOnDispatch: "Echo Flight departs. It cannot be stopped.",
      toastOnDuplicate: "ECHO: A new Echo Flight has materialized in your fleet. You now have {count}.",
      toastOnMaxReached: "Echo Flight has reached maximum resonance. 10 copies exist.",
      toastOnComplete: "Echo Flight returns... and another one appears."
    }
  },

  "market-maker": {
    name: "Market Maker",
    behavior: "fuel_chaos",
    description: "Wreaks havoc on fuel markets. 3 uses, then it retires to your museum.",
    config: {
      affectsFuelPrices: true,
      durationDays: 1, // Exactly 1 day of chaos
      priceRangeMin: 0.05, // $0.05/gal minimum
      priceRangeMax: 496.43, // $496.43/gal maximum - WILD swings
      maxUses: 3, // Retires to museum after 3 flights
      toastOnDispatch: "MARKET CHAOS: Fuel prices are going INSANE for 24 hours!",
      toastOnRetire: "Market Maker has retired to your trophy case after 3 legendary flights."
    }
  },

  "null-pointer": {
    name: "Null Pointer",
    behavior: "null_values",
    description: "All flight values return null. Stats display as undefined. Fake BSOD on landing.",
    config: {
      nullifyStats: true, // All stats show as "null" or "undefined"
      revenueNull: true, // Revenue displays as "null" but still credits normally
      triggerFakeBSOD: true, // Show BSOD on complete
      bsodText: "AIRLINE_OPS.exe has encountered a NULL_POINTER_EXCEPTION in flight.dll\n\nThe aircraft referenced memory at 0x00000000.\n\nClick anywhere to continue.",
      toastOnDispatch: "Null Pointer departs. All values are now undefined.",
      toastOnComplete: "Null Pointer arrived. Revenue: null. Passengers: null. Satisfaction: null."
    }
  },

  "probability-storm": {
    name: "Probability Storm",
    behavior: "all_or_nothing",
    description: "All random events happen, or none do.",
    config: {
      allEventsChance: 0.5,
      toastOnAll: "Probability Storm: EVERYTHING happened.",
      toastOnNone: "Probability Storm: Nothing happened. Suspiciously smooth."
    }
  },

  "the-debt": {
    name: "The Debt",
    behavior: "compound_debt",
    description: "Perfect stats. But each flight costs more than the last. Compound interest.",
    config: {
      baseFlightCost: 10000, // $100 base cost (in cents)
      compoundRate: 1.5, // Each flight costs 1.5x the previous
      trackFlightCount: true, // Track how many times flown
      toastOnDispatch: "The Debt takes flight. The cost compounds.",
      toastOnComplete: "Flight cost: ${cost}. Next flight will cost: ${nextCost}. The debt grows."
    }
  },

  "passenger-zero": {
    name: "Passenger Zero",
    behavior: "zombie_outbreak",
    description: "One flight. One outbreak. Permanent consequences.",
    config: {
      triggerOnFirstFlight: true,
      maxUses: 1, // One use then retire to museum
      toastOnRetire: "Passenger Zero has retired to your trophy case. The damage is done.",
      zombieNewsSequence: [
        // Week 1 - The Beginning
        { day: 0, text: "BREAKING: 3 passengers hospitalized after flight from {destination} with flu-like symptoms" },
        { day: 1, text: "CDC monitoring 'unusual illness cluster' linked to {destination} airport" },
        { day: 2, text: "7 more cases reported. Health officials urge calm" },
        { day: 3, text: "BREAKING: {destination} airport closes Terminal B for 'deep cleaning'" },
        { day: 4, text: "Patient zero identified as passenger on Flight 291" },
        { day: 5, text: "Hospital workers in {destination} report 'aggressive behavior' from infected patients" },
        { day: 6, text: "Social media videos show patients 'biting staff' - officials say 'misinformation'" },
        { day: 7, text: "CDC raises alert level. 47 confirmed cases" },
        // Week 2 - Local Spread
        { day: 8, text: "BREAKING: First case outside {destination} confirmed in neighboring county" },
        { day: 9, text: "National Guard deployed to {destination} General Hospital" },
        { day: 10, text: "Leaked memo: CDC internally calling it 'Reanimation Syndrome'" },
        { day: 11, text: "BREAKING: {destination} declares state of emergency" },
        { day: 12, text: "Flights from {destination} suspended by 3 major carriers" },
        { day: 13, text: "Video: Infected patient walks despite 'no vital signs' - doctors baffled" },
        { day: 14, text: "312 confirmed cases. 14 deaths. 9 'unexplained recoveries'" },
        { day: 15, text: "BREAKING: President addresses nation. 'Situation is under control'" },
        // Week 3 - Regional Crisis
        { day: 16, text: "Second city reports cases. Chicago O'Hare implements screening" },
        { day: 17, text: "WHO officially monitoring 'Novel Reanimation Pathogen'" },
        { day: 18, text: "BREAKING: Infected breach quarantine at {destination} hospital. 3 staff missing" },
        { day: 19, text: "Amateur footage shows 'shuffling figures' near {destination} suburbs" },
        { day: 20, text: "Walmart and Costco report 'unprecedented' run on supplies" },
        { day: 21, text: "FAA issues advisory: Avoid {destination} airspace" },
        { day: 22, text: "BREAKING: Cases confirmed in 7 states" },
        { day: 23, text: "Pentagon denies military involvement. Refuses further comment" },
        { day: 24, text: "Hospitals nationwide told to 'prepare for surge capacity'" },
        { day: 25, text: "First confirmed case in New York City" },
        // Week 4 - National Emergency
        { day: 26, text: "BREAKING: President declares National Emergency" },
        { day: 27, text: "Stock market drops 2,400 points at open" },
        { day: 28, text: "TSA implements mandatory health screening at all airports" },
        { day: 29, text: "FEMA activates emergency response. Citizens told to 'shelter in place'" },
        { day: 30, text: "Confirmed cases: 4,847. Deaths: 312. 'Reanimations': 284" },
        { day: 31, text: "BREAKING: Los Angeles reports first cases" },
        { day: 32, text: "Viral video: News anchor attacked on live TV. Broadcast cuts to static" },
        { day: 33, text: "Cell networks overloaded in major cities" },
        { day: 34, text: "BREAKING: Interstate travel restrictions announced" },
        { day: 35, text: "Gun stores report month-long backlog. Ammunition sold out nationwide" },
        // Week 5-6 - International Spread
        { day: 36, text: "BREAKING: Canada closes US border" },
        { day: 37, text: "First European case confirmed in London Heathrow" },
        { day: 38, text: "EU suspends all flights from United States" },
        { day: 39, text: "Mexico deploys military to northern border" },
        { day: 40, text: "BREAKING: Tokyo reports 'suspicious illness' in Narita airport worker" },
        { day: 41, text: "WHO declares Global Health Emergency" },
        { day: 42, text: "Sydney, Singapore, Dubai halt US flights" },
        { day: 43, text: "BREAKING: UK Prime Minister: 'We are not immune'" },
        { day: 44, text: "Paris CDG closes indefinitely after 'incident' in Terminal 2" },
        { day: 45, text: "Global confirmed cases: 47,000. Situation 'deteriorating rapidly'" },
        // Week 7-8 - Collapse Begins
        { day: 46, text: "BREAKING: US military authorized to use 'all necessary force'" },
        { day: 47, text: "Major airlines announce 'indefinite suspension' of operations" },
        { day: 48, text: "Power outages reported across Northeast" },
        { day: 49, text: "BREAKING: Atlanta airport overrun. Last flight departed 3:42 AM" },
        { day: 50, text: "CDC website goes offline" },
        { day: 51, text: "Emergency broadcast: 'Do not approach individuals displaying erratic behavior'" },
        { day: 52, text: "BREAKING: Chicago falls. Mayor's last broadcast: 'God help us all'" },
        { day: 53, text: "Satellite images show fires burning in 12 major cities" },
        { day: 54, text: "BREAKING: Pentagon relocates to undisclosed location" },
        { day: 55, text: "Ham radio reports: 'Safe zones' established in rural Montana, Wyoming" },
        // Week 9-10 - Global Collapse
        { day: 56, text: "BREAKING: London bridge barricades fall. Thames runs red" },
        { day: 57, text: "Last tweet from @WHO: 'Maintain hope. Help is coming.'" },
        { day: 58, text: "Berlin Wall rebuilt 'to keep them out'" },
        { day: 59, text: "BREAKING: Moscow goes dark. No communication in 72 hours" },
        { day: 60, text: "Australia declares martial law. 'Shoot on sight' orders issued" },
        { day: 61, text: "Tokyo Tower broadcasting loop: 'Stay indoors. Stay alive.'" },
        { day: 62, text: "BREAKING: UN headquarters evacuated. General Assembly 'presumed lost'" },
        { day: 63, text: "Pilots report seeing 'no lights' over North America at night" },
        { day: 64, text: "Last commercial flight: Emirates 231, Dubai to nowhere" },
        { day: 65, text: "Estimated global infected: 890 million" },
        // Week 11-12 - The New World
        { day: 66, text: "Automated systems still running: Traffic lights, airline websites, stock tickers" },
        { day: 67, text: "BREAKING: Someone is still updating Wikipedia. Last edit: 'List of extinct cities'" },
        { day: 68, text: "Planes still taking off on autopilot. No one aboard. No one landing them" },
        { day: 69, text: "Your airline reported Q3 earnings. Shareholders... unavailable for comment" },
        { day: 70, text: "BREAKING: Zombie spotted in business class. Upgraded from economy" },
        { day: 71, text: "Air traffic control silence. Except for one frequency: 'Is anyone out there?'" },
        { day: 72, text: "The dead walk. Your fleet keeps flying. This is fine" },
        { day: 73, text: "BREAKING: Automated tweet from Delta: 'Thanks for flying with us!'" },
        { day: 74, text: "Server farms still running. The cloud survives. Humanity... unclear" },
        { day: 75, text: "Your on-time performance: 94%. Civilization's on-time performance: 0%" },
        // Week 13-15 - Dark Humor Era
        { day: 78, text: "Zombies reportedly 'confused' by airport security procedures" },
        { day: 81, text: "TSA still requiring removal of shoes. Old habits die hard. Unlike people" },
        { day: 84, text: "BREAKING: Starbucks inside Terminal C still open. Barista is undead but making lattes" },
        { day: 87, text: "Airline loyalty program update: 'Earn miles even after death'" },
        { day: 90, text: "Your cargo planes still delivering packages. Amazon reviews: 'Delivery was fast. Driver tried to eat me. 3 stars'" },
        { day: 93, text: "In-flight magazine now featuring: '10 Best Bunkers to Visit This Fall'" },
        { day: 96, text: "Zombie spotted reading SkyMall catalog. Showed interest in neck pillow" },
        { day: 99, text: "BREAKING: FAA still issuing fines. No one left to pay them" },
        // Week 16-20 - Acceptance
        { day: 105, text: "Survivors establish 'New Denver' in underground airport tunnels" },
        { day: 112, text: "First zombie-safe airline route announced: Bunker A to Bunker B" },
        { day: 119, text: "Currency collapsed. Economy now runs on jet fuel and canned beans" },
        { day: 126, text: "Your airline valuation: Infinity (last surviving corporation)" },
        { day: 133, text: "BREAKING: Zombie union forms. Demands 'brains-life balance'" },
        { day: 140, text: "Scientists report zombies 'slowing down.' May starve within decade" },
        { day: 147, text: "First baby born in airport bunker. Named 'Boeing'" },
        { day: 154, text: "Zombie horde migration patterns now used for flight planning" },
        { day: 161, text: "BREAKING: Elon Musk's Mars colony sends message: 'Good luck down there lol'" },
        // Week 21+ - The Long Game
        { day: 170, text: "Year One. Your airline still operates. Humanity adapts" },
        { day: 180, text: "Zombies now classified as 'wildlife.' Hunting season opens in Texas" },
        { day: 200, text: "Reconstruction begins. Airports designated 'critical infrastructure'" },
        { day: 220, text: "First international flight in 8 months. Passengers cried at takeoff" },
        { day: 240, text: "BREAKING: Vaccine shows promise. Side effects include 'mild cravings'" },
        { day: 260, text: "Global population: 847 million. Down from 8 billion. 'Manageable'" },
        { day: 280, text: "Zombie tourism industry emerges. 'See where it all began!'" },
        { day: 300, text: "Your airline named 'Most Resilient Brand' by surviving marketing firm" },
        { day: 330, text: "Children now taught 'zombie drills' alongside fire drills" },
        { day: 365, text: "One year since Patient Zero. Your airline: Still flying. Still profitable. Still cursed" },
        // Beyond Year 1
        { day: 400, text: "Zombie population declining. Experts cite 'lack of fresh brains'" },
        { day: 450, text: "BREAKING: Last wild zombie captured. Displayed in Smithsonian" },
        { day: 500, text: "World returns to 'new normal.' Airports now have zombie-proof glass. Standard" },
        { day: 550, text: "History books updated. Chapter 47: 'The Passenger Zero Incident'" },
        { day: 600, text: "Your airline goes public again. IPO oversubscribed 400%" },
        { day: 700, text: "Memorial unveiled at {destination} airport. 'Never Forget. Always Sanitize'" },
        { day: 800, text: "Conspiracy theorists claim 'it was all fake.' Ignore the mass graves" },
        { day: 900, text: "Generation Z+ has no memory of the outbreak. Thinks zombies are 'vintage aesthetic'" },
        { day: 1000, text: "1000 days since Passenger Zero. You're still here. Your airline's still flying. Somehow, that's the scariest part" }
      ],
      permanent: true,
      toastOnFirstFlight: "Patient Zero has landed. Something has begun."
    }
  },

  "timeline-fork": {
    name: "Timeline Fork",
    behavior: "choice_outcome",
    description: "Choose between two outcomes when flight completes.",
    config: {
      presentChoice: true,
      choiceA: {
        label: "High Revenue",
        revenueMultiplier: 2.0,
        reputationChange: -100
      },
      choiceB: {
        label: "High Reputation",
        revenueMultiplier: 0.5,
        reputationChange: +200
      },
      toastOnChoice: "Timeline Fork: Choose your reality."
    }
  },

  "zero-divided-by-zero": {
    name: "Zero Divided by Zero",
    behavior: "mathematical_impossibility",
    description: "Capacity: 0/Infinity. Can't fly. Appears everywhere. Always crashed. Always fine.",
    config: {
      canDispatch: false,
      dispatchBlockMessage: "ERROR: Cannot calculate route for Zero Divided by Zero.",
      randomMapAppearance: true,
      appearanceLocations: "random_multiple",
      displayStatus: ["Departed", "Delayed", "Cancelled", "Arrived"],
      capacityDisplay: "0/Infinity",
      reliabilityDisplay: "100% / NaN%",
      toastOnAcquire: "You now own Zero Divided by Zero. We're not sure what that means."
    }
  },

  "the-code": {
    name: "The Code",
    behavior: "bug_squash_minigame",
    description: "eJwryUhVyCtNzklNLFJIzk9JLVYoLU5NUSjJV0hKVTCAAgD7JwvV",
    config: {
      triggerMinigame: true,
      minigameType: "bug_squash",
      toastOnDispatch: "Squash some bugs!",
      toastOnComplete: "HIGH SCORE ACHIEVED. Thanks for playing.",
      dedicationText: "The little pixel crab flies again."
    }
  },

  "the-anchor": {
    name: "The Anchor",
    behavior: "slow_flight",
    description: "1 knot cruise speed. JFK to LAX takes 97 days. Cannot be sold.",
    config: {
      // The aircraft's 1 knot speed is in the database, this behavior just prevents selling
      cannotSell: true,
      cannotScrap: true,
      toastOnDispatch: "The Anchor begins its journey. See you in a few months.",
      toastOnComplete: "The Anchor has finally arrived. Somehow."
    }
  }
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Generate random integer between min and max (inclusive)
 */
function randomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

/**
 * Generate random float between min and max
 */
function randomFloat(min, max) {
  return Math.random() * (max - min) + min;
}

/**
 * Get random airport from array
 */
function getRandomAirport(airports) {
  if (!airports || airports.length === 0) return null;
  return airports[Math.floor(Math.random() * airports.length)];
}

/**
 * Format currency for display
 */
function formatCurrency(amount) {
  return '$' + Math.abs(amount).toLocaleString();
}

// =============================================================================
// CORE HANDLER FUNCTION
// =============================================================================

/**
 * Process anomalous aircraft behavior
 * Called at various flight lifecycle points
 *
 * @param {string} aircraftId - The aircraft ID
 * @param {string} event - Lifecycle event: 'dispatch', 'takeoff', 'inflight', 'landing', 'complete', 'manual_land', 'manual_stop'
 * @param {Object} flightData - Current flight data
 * @param {Object} gameState - Current game state
 * @returns {Object} - { flightData, sideEffects, blocked?, blockMessage? }
 */
export function processAnomalousBehavior(aircraftId, event, flightData, gameState) {
  const behaviorDef = ANOMALOUS_BEHAVIORS[aircraftId];
  if (!behaviorDef) return { flightData, sideEffects: [] };

  const sideEffects = [];
  let modifiedFlight = { ...flightData };

  try {
    switch (behaviorDef.behavior) {

      // =========================================================================
      // REVERSE_OTHER_FLIGHTS - Agee-Whells
      // All other flights reverse course when this one dispatches
      // =========================================================================
      case "reverse_other_flights":
        if (event === "dispatch") {
          // Show ANOMALY DETECTED modal on dispatch
          if (behaviorDef.config.modalOnDispatch) {
            sideEffects.push({
              type: "anomaly_modal",
              modal: behaviorDef.config.modalOnDispatch,
              aircraftId: aircraftId,
            });
          }
          // Side effect to reverse all other flights
          sideEffects.push({
            type: "reverse_all_flights",
            excludeFlightId: modifiedFlight.id
          });
          sideEffects.push({ type: "toast", message: behaviorDef.config.toastOnDispatch });
        }
        break;

      // =========================================================================
      // VERTICAL_ONLY - VTOnly
      // =========================================================================
      case "vertical_only":
        if (event === "dispatch") {
          modifiedFlight.animation = "vertical";
          modifiedFlight.destination = modifiedFlight.origin; // Fly to same airport (goes up, comes down)
          modifiedFlight.actualDestination = modifiedFlight.origin;
          modifiedFlight.distanceTraveled = 0;
          modifiedFlight.instantComplete = true; // Complete immediately since it's just up/down
        }
        if (event === "complete") {
          sideEffects.push({ type: "toast", message: behaviorDef.config.toastOnComplete });
        }
        break;

      // =========================================================================
      // BERMUDA_REDIRECT - The Bermudan
      // =========================================================================
      case "bermuda_redirect":
        if (event === "dispatch") {
          modifiedFlight.originalDestination = modifiedFlight.destination;
          modifiedFlight.destination = behaviorDef.config.forceDestination;
          sideEffects.push({ type: "toast", message: behaviorDef.config.toastOnDispatch });
        }
        break;

      // =========================================================================
      // NIGHT_SMUGGLER - The Moonshiner
      // =========================================================================
      case "night_smuggler":
        if (event === "dispatch") {
          const hour = gameState.currentHour || 12;
          const inWindow = hour >= behaviorDef.config.dispatchWindowStart ||
                          hour < behaviorDef.config.dispatchWindowEnd;
          if (!inWindow) {
            return {
              blocked: true,
              blockMessage: behaviorDef.config.blockDispatchMessage,
              flightData: modifiedFlight,
              sideEffects: []
            };
          }
        }
        if (event === "complete") {
          if (Math.random() < behaviorDef.config.fineChance) {
            sideEffects.push({ type: "fine", amount: behaviorDef.config.fineAmount });
            sideEffects.push({
              type: "ground_aircraft",
              aircraftId: aircraftId,
              days: behaviorDef.config.groundedDays
            });
            sideEffects.push({ type: "toast", message: behaviorDef.config.toastOnFine });
          } else {
            sideEffects.push({ type: "toast", message: behaviorDef.config.toastOnSuccess });
          }
        }
        break;

      // =========================================================================
      // BATHTUB_FLIGHT - Spruce Moose
      // =========================================================================
      case "bathtub_flight":
        if (event === "dispatch") {
          modifiedFlight.instantComplete = true;
          modifiedFlight.revenue = 0.01;
        }
        if (event === "complete") {
          sideEffects.push({ type: "toast", message: behaviorDef.config.toastOnComplete });
          // Permanently ground the aircraft - it becomes a museum piece
          if (behaviorDef.config.permanentGround) {
            sideEffects.push({
              type: "permanent_ground",
              aircraftId: aircraftId,
              message: "Spruce Moose has been retired to your trophy case."
            });
          }
        }
        break;

      // =========================================================================
      // QUANTUM_TIME - Quantum Hopper (10 jumps then museum)
      // May arrive before departure - time travel theme
      // =========================================================================
      case "quantum_time": {
        const currentJumps = gameState?.aircraftUses?.[aircraftId] || 0;
        const maxJumps = behaviorDef.config.maxJumps || 10;

        if (event === "dispatch") {
          // Check if already retired
          if (currentJumps >= maxJumps) {
            return {
              flightData: modifiedFlight,
              sideEffects: [],
              blocked: true,
              blockMessage: "Quantum Hopper has collapsed into your museum."
            };
          }

          // Determine quantum outcome
          const roll = Math.random();
          if (roll < 0.5) {
            // Arrive "yesterday" - instant complete with time travel flag
            modifiedFlight.instantComplete = true;
            modifiedFlight.arrivedYesterday = true;
            sideEffects.push({ type: "toast", message: behaviorDef.config.toastOnTimeTravel });
          } else if (roll < 0.8) {
            // Instant arrival
            modifiedFlight.instantComplete = true;
            sideEffects.push({ type: "toast", message: behaviorDef.config.toastOnInstant });
          } else {
            // Normal flight (rare)
            sideEffects.push({ type: "toast", message: behaviorDef.config.toastOnNormal });
          }
        }

        if (event === "complete") {
          // Track this jump
          const newCount = currentJumps + 1;
          sideEffects.push({
            type: "increment_aircraft_use",
            aircraftId: aircraftId,
            newCount: newCount
          });

          // Show jump progress
          sideEffects.push({
            type: "toast",
            message: behaviorDef.config.toastOnJump
              .replace("{current}", newCount)
              .replace("{max}", maxJumps)
          });

          // Check if this is the last jump - retire to museum
          if (newCount >= maxJumps) {
            sideEffects.push({
              type: "toast",
              message: behaviorDef.config.toastOnRetire
            });
            sideEffects.push({
              type: "permanent_ground",
              aircraftId: aircraftId,
              message: behaviorDef.config.toastOnRetire
            });
          }
        }
        break;
      }

      // =========================================================================
      // RETURN_TO_ORIGIN - The Mobius
      // Always flies back to where it started. Can never truly arrive elsewhere.
      // =========================================================================
      case "return_to_origin":
        if (event === "dispatch") {
          // Store the original destination for display purposes
          modifiedFlight.originalDestination = modifiedFlight.destination;
          // But force it to return to origin
          modifiedFlight.destination = modifiedFlight.origin;
          modifiedFlight.actualDestination = modifiedFlight.origin;
          modifiedFlight.animation = "mobius_strip";
          sideEffects.push({ type: "toast", message: behaviorDef.config.toastOnDispatch });
        }
        if (event === "complete") {
          sideEffects.push({ type: "toast", message: behaviorDef.config.toastOnComplete });
        }
        break;

      // =========================================================================
      // NEGATIVE_PASSENGERS - Schrodinger Jet
      // Carries -1 passengers but still makes money
      // =========================================================================
      case "negative_passengers":
        if (event === "dispatch") {
          modifiedFlight.passengerCount = -1; // Negative passengers
          modifiedFlight.quantumVisual = true; // Glitchy appearance
          sideEffects.push({ type: "toast", message: behaviorDef.config.toastOnDispatch });
        }
        if (event === "complete") {
          // Still generates positive revenue somehow
          modifiedFlight.revenuePositive = true;
          sideEffects.push({ type: "toast", message: behaviorDef.config.toastOnComplete });
        }
        break;

      // =========================================================================
      // NEGATIVE_STATS - Negative One
      // =========================================================================
      case "negative_stats":
        if (event === "complete") {
          sideEffects.push({ type: "add_fuel", amount: Math.abs(behaviorDef.config.fuelConsumed) });
          sideEffects.push({ type: "revenue", amount: behaviorDef.config.revenuePerFlight });
          sideEffects.push({ type: "toast", message: behaviorDef.config.toastOnComplete });
        }
        break;

      // =========================================================================
      // GHOST_OPERATION - Ghost Freight
      // No crew, no fuel, no cargo - still profitable
      // =========================================================================
      case "ghost_operation":
        if (event === "dispatch") {
          modifiedFlight.skipCrewRequirement = true;
          modifiedFlight.skipFuelRequirement = true;
          modifiedFlight.skipCargoLoading = true;
          modifiedFlight.ghostVisual = true; // Semi-transparent on map
          sideEffects.push({ type: "toast", message: behaviorDef.config.toastOnDispatch });
        }
        if (event === "complete") {
          sideEffects.push({ type: "toast", message: behaviorDef.config.toastOnComplete });
        }
        break;

      // =========================================================================
      // ETERNAL_FLIGHT - The Perpetual (circumnavigates equator for 3 days)
      // =========================================================================
      case "eternal_flight": {
        const waypoints = behaviorDef.config.equatorWaypoints || ["SEQM", "DNMM", "VRMM", "WIII"];

        if (event === "dispatch") {
          modifiedFlight.neverLands = true; // Flies forever
          modifiedFlight.neverAutoComplete = true;
          modifiedFlight.passiveIncome = behaviorDef.config.passiveIncomePerHour;
          modifiedFlight.flightPath = behaviorDef.config.flightPath;

          // Override route to equator circumnavigation
          // Start from first waypoint, head to second
          modifiedFlight.equatorWaypoints = waypoints;
          modifiedFlight.currentWaypointIndex = 0;
          modifiedFlight.origin = waypoints[0]; // Quito
          modifiedFlight.destination = waypoints[1]; // Lagos
          modifiedFlight.originalOrigin = flightData.origin; // Remember where user wanted to fly from
          modifiedFlight.originalDestination = flightData.destination; // Remember where user wanted to fly to

          sideEffects.push({ type: "toast", message: behaviorDef.config.toastOnDispatch });
        }

        // The Perpetual never auto-completes - only manual stop can land it
        if (event === "manual_stop") {
          sideEffects.push({
            type: "toast",
            message: behaviorDef.config.toastOnManualStop
          });
        }

        if (event === "complete") {
          // Only happens if manually stopped - The Perpetual finally lands
          sideEffects.push({
            type: "toast",
            message: behaviorDef.config.toastOnManualStop
          });
        }
        break;
      }

      // =========================================================================
      // INVISIBLE_FLIGHT - Blackout
      // Disappears from radar during flight
      // =========================================================================
      case "invisible_flight":
        if (event === "dispatch") {
          modifiedFlight.invisibleOnMap = true; // Don't render on world map
          modifiedFlight.skipFlightLog = true; // No entry in flight log
          modifiedFlight.ignoreWeatherPenalties = true;
          modifiedFlight.ignoreTurbulence = true;
          sideEffects.push({ type: "toast", message: behaviorDef.config.toastOnDispatch });
        }
        if (event === "complete") {
          sideEffects.push({ type: "toast", message: behaviorDef.config.toastOnComplete });
        }
        break;

      // =========================================================================
      // PASSIVE_INCOME - Revenue Engine
      // Can't fly but generates $10M/day just by existing
      // =========================================================================
      case "passive_income":
        if (event === "dispatch") {
          // Cannot be dispatched - block it
          return {
            blocked: true,
            blockMessage: behaviorDef.config.blockDispatchMessage,
            flightData: modifiedFlight,
            sideEffects: []
          };
        }
        // Note: Day-end passive income is handled in EndOfDaySystem, not here
        // This behavior definition just provides the config
        break;

      // =========================================================================
      // INSTANT_ARRIVAL - Yesterday
      // You'd have to have been here yesterday to see it fly.
      // =========================================================================
      case "instant_arrival":
        if (event === "dispatch") {
          // Don't actually fly - just show the joke and retire to museum
          modifiedFlight.instantComplete = true;
          modifiedFlight.skipFlight = true; // Signal to not create actual flight
          sideEffects.push({
            type: "toast",
            message: "The Yesterday took off. But you'd have to have been here yesterday to see it."
          });
          sideEffects.push({
            type: "permanent_ground",
            aircraftId: "yesterday",
            message: "The Yesterday is now a museum piece. It flew yesterday."
          });
        }
        break;

      // =========================================================================
      // CAUSES_OTHER_FAILURES - The Jinx
      // Every flight causes another random flight to fail
      // =========================================================================
      case "causes_other_failures":
        if (event === "dispatch") {
          // This flight is guaranteed to succeed
          modifiedFlight.guaranteedSuccess = true;
          modifiedFlight.immuneToEvents = true;
          // But cause another flight to fail
          sideEffects.push({
            type: "fail_random_flight",
            excludeFlightId: modifiedFlight.id,
            message: behaviorDef.config.toastOnOtherFail
          });
          sideEffects.push({ type: "toast", message: behaviorDef.config.toastOnDispatch });
        }
        if (event === "complete") {
          sideEffects.push({ type: "toast", message: behaviorDef.config.toastOnComplete });
        }
        break;

      // =========================================================================
      // FIGURE_EIGHT_FOREVER - Infinite Loop
      // Flies figure-8s forever, never lands on its own
      // =========================================================================
      case "figure_eight_forever":
        if (event === "dispatch") {
          modifiedFlight.animation = "figure_eight";
          modifiedFlight.neverLands = true; // Will not auto-complete
          modifiedFlight.passiveIncome = behaviorDef.config.passiveIncomePerHour || 8888;
          modifiedFlight.allStatsAre8 = true; // Visual gag
          sideEffects.push({ type: "toast", message: behaviorDef.config.toastOnDispatch });
        }
        if (event === "manual_stop") {
          // Only way to stop this plane
          sideEffects.push({ type: "toast", message: behaviorDef.config.toastOnManualStop });
        }
        // Note: "complete" event will never fire naturally for this aircraft
        break;

      // =========================================================================
      // SPACE_FOLD - The Fold
      // Instant travel but costs $47M in exotic fuel
      // =========================================================================
      case "space_fold":
        if (event === "dispatch") {
          modifiedFlight.instantComplete = true;
          modifiedFlight.ignoreDistance = true;
          // Charge the massive fuel cost
          sideEffects.push({
            type: "charge_fuel_cost",
            amountCents: behaviorDef.config.fuelCostCents || 4700000000
          });
          sideEffects.push({ type: "toast", message: behaviorDef.config.toastOnDispatch });
        }
        if (event === "complete") {
          sideEffects.push({ type: "toast", message: behaviorDef.config.toastOnComplete });
        }
        break;

      // =========================================================================
      // DUPLICATES_SELF - Echo Flight (creates copies of itself)
      // =========================================================================
      case "duplicates_self": {
        const echoCount = gameState?.echoFlightCount || 1;
        const maxDuplicates = behaviorDef.config.maxDuplicates || 10;

        if (event === "dispatch") {
          modifiedFlight.autoRedispatch = true;
          modifiedFlight.cannotUnschedule = true;
          sideEffects.push({ type: "toast", message: behaviorDef.config.toastOnDispatch });
        }

        if (event === "complete") {
          // Auto-redispatch immediately
          sideEffects.push({
            type: "redispatch",
            origin: modifiedFlight.origin,
            destination: modifiedFlight.destination,
            aircraftId: aircraftId
          });

          // Create a duplicate if under max
          if (echoCount < maxDuplicates) {
            sideEffects.push({
              type: "duplicate_aircraft",
              aircraftId: "echo-flight",
              newCount: echoCount + 1
            });
            sideEffects.push({
              type: "toast",
              message: behaviorDef.config.toastOnDuplicate.replace("{count}", echoCount + 1)
            });
          } else {
            sideEffects.push({
              type: "toast",
              message: behaviorDef.config.toastOnMaxReached
            });
          }
        }
        break;
      }

      // =========================================================================
      // FUEL_CHAOS - Market Maker (3 uses then museum)
      // =========================================================================
      case "fuel_chaos":
        if (event === "dispatch") {
          // Check uses - aircraft tracks its own flight count
          const currentUses = gameState?.aircraftUses?.[aircraftId] || 0;
          const maxUses = behaviorDef.config.maxUses || 999;

          if (currentUses >= maxUses) {
            // Already used up - don't fly, should be in museum
            return {
              flightData: modifiedFlight,
              sideEffects: [],
              blocked: true,
              blockMessage: "Market Maker has already retired to your trophy case."
            };
          }

          // Duration can be fixed number or range
          const durationDays = typeof behaviorDef.config.durationDays === 'number'
            ? behaviorDef.config.durationDays
            : randomInt(behaviorDef.config.durationDays.min, behaviorDef.config.durationDays.max);

          sideEffects.push({
            type: "fuel_market_chaos",
            durationDays: durationDays,
            priceMin: behaviorDef.config.priceRangeMin,
            priceMax: behaviorDef.config.priceRangeMax
          });
          sideEffects.push({ type: "toast", message: behaviorDef.config.toastOnDispatch });

          // Track this use
          sideEffects.push({
            type: "increment_aircraft_use",
            aircraftId: aircraftId,
            newCount: currentUses + 1
          });

          // Check if this is the last use - retire to museum
          if (currentUses + 1 >= maxUses) {
            sideEffects.push({
              type: "permanent_ground",
              aircraftId: aircraftId,
              message: behaviorDef.config.toastOnRetire || "Aircraft retired to museum."
            });
          }
        }
        break;

      // =========================================================================
      // NULL_VALUES - Null Pointer
      // All stats show as null/undefined, BSOD on landing
      // =========================================================================
      case "null_values":
        if (event === "dispatch") {
          modifiedFlight.nullifyStats = true; // Display all stats as "null"
          modifiedFlight.revenueDisplay = "null"; // Show "null" instead of amount
          sideEffects.push({ type: "toast", message: behaviorDef.config.toastOnDispatch });
        }
        if (event === "complete") {
          sideEffects.push({ type: "show_fake_bsod", text: behaviorDef.config.bsodText });
          sideEffects.push({ type: "toast", message: behaviorDef.config.toastOnComplete, delay: true });
        }
        break;

      // =========================================================================
      // ALL_OR_NOTHING - Probability Storm
      // =========================================================================
      case "all_or_nothing":
        if (event === "inflight") {
          if (Math.random() < behaviorDef.config.allEventsChance) {
            sideEffects.push({ type: "trigger_all_events" });
            sideEffects.push({ type: "toast", message: behaviorDef.config.toastOnAll });
          } else {
            sideEffects.push({ type: "suppress_all_events" });
            sideEffects.push({ type: "toast", message: behaviorDef.config.toastOnNone });
          }
        }
        break;

      // =========================================================================
      // COMPOUND_DEBT - The Debt
      // Each flight costs more than the last (compound interest)
      // =========================================================================
      case "compound_debt": {
        const flightCount = gameState?.aircraftUses?.[aircraftId] || 0;
        const baseCost = behaviorDef.config.baseFlightCost || 10000; // $100 in cents
        const compoundRate = behaviorDef.config.compoundRate || 1.5;
        const currentCost = Math.round(baseCost * Math.pow(compoundRate, flightCount));
        const nextCost = Math.round(baseCost * Math.pow(compoundRate, flightCount + 1));

        if (event === "dispatch") {
          sideEffects.push({ type: "toast", message: behaviorDef.config.toastOnDispatch });
        }
        if (event === "complete") {
          // Charge the compounded cost
          sideEffects.push({
            type: "revenue",
            amount: -currentCost // Negative = cost
          });
          // Track this flight
          sideEffects.push({
            type: "increment_aircraft_use",
            aircraftId: aircraftId,
            newCount: flightCount + 1
          });
          sideEffects.push({
            type: "toast",
            message: behaviorDef.config.toastOnComplete
              .replace("{cost}", formatCurrency(currentCost / 100))
              .replace("{nextCost}", formatCurrency(nextCost / 100))
          });
        }
        break;
      }

      // =========================================================================
      // ZOMBIE_OUTBREAK - Passenger Zero
      // =========================================================================
      case "zombie_outbreak":
        if (event === "complete" && !gameState.zombieOutbreakStarted) {
          // Start the zombie outbreak
          sideEffects.push({
            type: "start_zombie_outbreak",
            newsSequence: behaviorDef.config.zombieNewsSequence,
            destination: modifiedFlight.destination
          });
          sideEffects.push({ type: "toast", message: behaviorDef.config.toastOnFirstFlight });

          // Retire to museum after one use (the damage is done)
          if (behaviorDef.config.maxUses === 1) {
            sideEffects.push({ type: "permanent_ground", aircraftId });
            sideEffects.push({ type: "increment_uses", aircraftId });
          }
        }
        break;

      // =========================================================================
      // CHOICE_OUTCOME - Timeline Fork
      // =========================================================================
      case "choice_outcome":
        if (event === "complete") {
          sideEffects.push({
            type: "present_choice",
            choiceA: behaviorDef.config.choiceA,
            choiceB: behaviorDef.config.choiceB,
            message: behaviorDef.config.toastOnChoice
          });
        }
        break;

      // =========================================================================
      // MATHEMATICAL_IMPOSSIBILITY - Zero Divided by Zero
      // =========================================================================
      case "mathematical_impossibility":
        if (event === "dispatch") {
          return {
            blocked: true,
            blockMessage: behaviorDef.config.dispatchBlockMessage,
            flightData: modifiedFlight,
            sideEffects: []
          };
        }
        // This plane just exists. It doesn't fly. But it appears on the map randomly.
        break;

      // =========================================================================
      // ARCADE_MODE - The Code
      // Triggers a Space Invaders-style mini-game on dispatch.
      // =========================================================================
      case "bug_squash_minigame":
        if (event === "dispatch") {
          sideEffects.push({
            type: "toast",
            message: behaviorDef.config.toastOnDispatch
          });
          sideEffects.push({
            type: "trigger_minigame",
            minigameType: behaviorDef.config.minigameType,
            dedication: behaviorDef.config.dedicationText
          });
        }
        if (event === "complete") {
          sideEffects.push({
            type: "toast",
            message: behaviorDef.config.toastOnComplete
          });
        }
        break;

      // =========================================================================
      // SLOW_FLIGHT - The Anchor
      // 1 knot cruise speed, cannot be sold/scrapped
      // =========================================================================
      case "slow_flight":
        if (event === "dispatch") {
          sideEffects.push({
            type: "toast",
            message: behaviorDef.config.toastOnDispatch
          });
        }
        if (event === "complete") {
          sideEffects.push({
            type: "toast",
            message: behaviorDef.config.toastOnComplete
          });
        }
        break;

      default:
        // Unknown behavior - proceed normally
        break;
    }
  } catch (error) {
    // Fail safe - if anomalous behavior throws, let flight proceed normally
    console.error(`[AnomalousBehaviors] Error processing ${aircraftId}:`, error);
    return { flightData, sideEffects: [] };
  }

  return { flightData: modifiedFlight, sideEffects };
}

// =============================================================================
// SIDE EFFECTS PROCESSOR
// =============================================================================

/**
 * Process side effects returned by anomalous behavior
 * This should be called by the core flight system after processAnomalousBehavior
 *
 * @param {Object[]} sideEffects - Array of side effect objects
 * @param {Object} gameState - Game state (will be mutated)
 * @param {Function} showToast - Toast display function
 * @param {Object} handlers - Optional custom handlers for specific effects
 */
export function processSideEffects(sideEffects, gameState, showToast, handlers = {}) {
  for (const effect of sideEffects) {
    try {
      switch (effect.type) {
        case "toast":
          if (effect.delay) {
            setTimeout(() => showToast?.(effect.message), 2000);
          } else {
            showToast?.(effect.message);
          }
          break;

        case "fine":
          if (gameState.cash !== undefined) {
            gameState.cash -= effect.amount;
          }
          break;

        case "ground_aircraft":
          handlers.groundAircraft?.(effect.aircraftId, effect.days);
          break;

        case "add_fuel":
          if (gameState.fuel !== undefined) {
            gameState.fuel += effect.amount;
          }
          break;

        case "revenue":
          if (gameState.cash !== undefined) {
            gameState.cash += effect.amount;
          }
          break;

        case "reduce_interest":
          if (gameState.loanInterestRate !== undefined) {
            gameState.loanInterestRate -= effect.percent;
          }
          break;

        case "fuel_market_chaos":
          handlers.startFuelMarketChaos?.(effect.durationDays, effect.priceMin, effect.priceMax);
          break;

        case "show_fake_bsod":
          showFakeBSOD(effect.text);
          break;

        case "show_schrodinger_crash":
          showSchrodingerCrash();
          break;

        case "start_zombie_outbreak":
          handlers.startZombieOutbreak?.(effect.newsSequence, effect.destination);
          break;

        case "present_choice":
          handlers.showTimelineForkChoice?.(effect.choiceA, effect.choiceB, effect.message);
          break;

        case "redispatch":
          handlers.scheduleRedispatch?.(effect.origin, effect.destination, effect.aircraftId);
          break;

        case "trigger_event":
          handlers.triggerFlightEvent?.(effect.eventType, effect.nonCancelling);
          break;

        case "trigger_all_events":
          handlers.triggerAllFlightEvents?.();
          break;

        case "suppress_all_events":
          handlers.suppressAllFlightEvents?.();
          break;

        case "reverse_all_flights":
          handlers.reverseAllFlights?.(effect.excludeFlightId);
          break;

        case "charge_fuel_cost":
          if (gameState.cash !== undefined) {
            gameState.cash -= effect.amountCents;
          }
          break;

        case "fail_random_flight":
          handlers.failRandomFlight?.(effect.excludeFlightId, effect.message);
          break;

        case "duplicate_aircraft":
          handlers.duplicateAircraft?.(effect.aircraftId, effect.newCount);
          break;

        case "increment_aircraft_use":
          if (!gameState.aircraftUses) gameState.aircraftUses = {};
          gameState.aircraftUses[effect.aircraftId] = effect.newCount;
          break;

        case "anomaly_modal":
          handlers.showAnomalyModal?.(effect.modal, effect.aircraftId);
          break;

        case "permanent_ground":
          handlers.permanentGroundAircraft?.(effect.aircraftId, effect.message);
          break;

        case "trigger_minigame":
          handlers.triggerMinigame?.(effect.minigameType, effect.dedication);
          break;

        default:
          console.warn(`[AnomalousBehaviors] Unknown side effect type: ${effect.type}`);
      }
    } catch (error) {
      console.error(`[AnomalousBehaviors] Error processing side effect:`, effect, error);
    }
  }
}

// =============================================================================
// SPECIAL COMPONENTS
// =============================================================================

/**
 * Show Schrodinger crash visual effect (Schrodinger Jet behavior)
 * Brief overlay showing both "ARRIVED" and "CRASHED" simultaneously
 */
export function showSchrodingerCrash() {
  if (typeof document === 'undefined') return;

  const overlay = document.createElement('div');
  overlay.id = 'schrodinger-crash';
  overlay.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: linear-gradient(90deg, rgba(0,255,0,0.3) 50%, rgba(255,0,0,0.3) 50%);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 99998;
    pointer-events: none;
    animation: schrodinger-flash 0.5s ease-out forwards;
  `;

  overlay.innerHTML = `
    <style>
      @keyframes schrodinger-flash {
        0% { opacity: 1; }
        100% { opacity: 0; }
      }
      @keyframes text-glitch {
        0%, 100% { transform: translateX(0); }
        25% { transform: translateX(-5px); }
        75% { transform: translateX(5px); }
      }
    </style>
    <div style="display: flex; width: 100%; justify-content: space-around;">
      <div style="
        font-size: 72px;
        font-weight: bold;
        color: #00ff00;
        text-shadow: 0 0 20px #00ff00;
        animation: text-glitch 0.1s infinite;
      ">ARRIVED</div>
      <div style="
        font-size: 72px;
        font-weight: bold;
        color: #ff0000;
        text-shadow: 0 0 20px #ff0000;
        animation: text-glitch 0.1s infinite;
      ">CRASHED</div>
    </div>
  `;

  document.body.appendChild(overlay);

  // Remove after animation
  setTimeout(() => overlay.remove(), 500);
}

/**
 * Show fake BSOD overlay (Null Pointer behavior)
 */
export function showFakeBSOD(text) {
  // Only run in browser environment
  if (typeof document === 'undefined') return;

  const bsodOverlay = document.createElement('div');
  bsodOverlay.id = 'fake-bsod';
  bsodOverlay.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: #5B21B6;
    color: white;
    font-family: 'Segoe UI', monospace;
    font-size: 18px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    z-index: 99999;
    cursor: pointer;
  `;

  bsodOverlay.innerHTML = `
    <div style="font-size: 100px; margin-bottom: 20px;">:(</div>
    <div style="max-width: 600px; text-align: left; white-space: pre-wrap;">${text}</div>
    <div style="margin-top: 40px; font-size: 16px; font-weight: bold; color: #ff4444; text-shadow: 0 0 10px rgba(255, 68, 68, 0.5);">
      YOUR COMPUTER IS FINE. THIS IS FAKE.
    </div>
    <div style="margin-top: 10px; font-size: 14px; opacity: 0.7;">
      (Click anywhere to continue)
    </div>
  `;

  bsodOverlay.addEventListener('click', () => {
    bsodOverlay.remove();
  });

  document.body.appendChild(bsodOverlay);
}

// =============================================================================
// ZERO DIVIDED BY ZERO - SPECIAL MAP BEHAVIOR
// =============================================================================

/**
 * Update Zero Divided by Zero positions on map
 * Called every map render tick
 */
export function updateZeroDividedByZeroPositions(gameState) {
  if (!gameState.collectedAircraft?.includes('zero-divided-by-zero')) return;

  // Every 30 seconds (assuming 10 ticks/second = 300 ticks)
  const tickInterval = 300;
  if ((gameState.tick || 0) % tickInterval === 0) {
    const numLocations = randomInt(3, 5);
    gameState.zeroDividedByZeroLocations = [];

    const airports = gameState.allAirports || [];
    for (let i = 0; i < numLocations && airports.length > 0; i++) {
      const airport = getRandomAirport(airports);
      if (airport) {
        gameState.zeroDividedByZeroLocations.push(airport.icao || airport);
      }
    }
  }
}

// =============================================================================
// ZOMBIE NEWS TICKER SYSTEM
// =============================================================================

/**
 * Start zombie outbreak news sequence
 */
export function startZombieOutbreak(gameState, newsSequence, patientZeroCity) {
  gameState.zombieOutbreakStarted = true;
  gameState.zombieOutbreakDay = gameState.currentDay || 0;
  gameState.zombieNewsSequence = newsSequence.map(item => ({
    ...item,
    text: item.text.replace("{destination}", patientZeroCity),
    shown: false
  }));
}

/**
 * Update zombie news - called on day advance
 */
export function updateZombieNews(gameState, addToNewsTicker) {
  if (!gameState.zombieOutbreakStarted) return;

  const daysSinceOutbreak = (gameState.currentDay || 0) - gameState.zombieOutbreakDay;

  for (const newsItem of gameState.zombieNewsSequence || []) {
    if (newsItem.day === daysSinceOutbreak && !newsItem.shown) {
      addToNewsTicker?.(newsItem.text);
      newsItem.shown = true;
    }
  }
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Check if an aircraft has anomalous behavior
 */
export function hasAnomalousBehavior(aircraftId) {
  return aircraftId in ANOMALOUS_BEHAVIORS;
}

/**
 * Get anomalous behavior definition for an aircraft
 */
export function getAnomalousBehavior(aircraftId) {
  return ANOMALOUS_BEHAVIORS[aircraftId] || null;
}

/**
 * Get all anomalous aircraft IDs
 */
export function getAllAnomalousAircraftIds() {
  return Object.keys(ANOMALOUS_BEHAVIORS);
}

// =============================================================================
// GROUNDED AIRCRAFT TRACKING
// =============================================================================

// In-memory store for grounded aircraft
// Format: { aircraftId: groundedUntilDay }
const groundedAircraftStore = {};

/**
 * Ground an aircraft for a number of days
 * @param {string} aircraftId - Aircraft ID
 * @param {number} days - Days to ground
 * @param {number} currentDay - Current game day
 */
export function groundAircraft(aircraftId, days, currentDay) {
  groundedAircraftStore[aircraftId] = currentDay + days;
  debug(`[Anomalous] Aircraft ${aircraftId} grounded until day ${groundedAircraftStore[aircraftId]}`);
}

/**
 * Check if an aircraft is currently grounded
 * @param {string} aircraftId - Aircraft ID
 * @param {number} currentDay - Current game day
 * @returns {boolean}
 */
export function isAircraftGrounded(aircraftId, currentDay) {
  const groundedUntil = groundedAircraftStore[aircraftId];
  if (!groundedUntil) return false;

  if (currentDay >= groundedUntil) {
    // Grounding expired - clean up
    delete groundedAircraftStore[aircraftId];
    return false;
  }

  return true;
}

/**
 * Get remaining grounded days for an aircraft
 * @param {string} aircraftId - Aircraft ID
 * @param {number} currentDay - Current game day
 * @returns {number} - Days remaining, or 0 if not grounded
 */
export function getGroundedDaysRemaining(aircraftId, currentDay) {
  const groundedUntil = groundedAircraftStore[aircraftId];
  if (!groundedUntil) return 0;
  return Math.max(0, groundedUntil - currentDay);
}

/**
 * Clear all grounded aircraft (for game reset)
 */
export function clearGroundedAircraft() {
  Object.keys(groundedAircraftStore).forEach(key => delete groundedAircraftStore[key]);
}

// =============================================================================
// EXPORTS
// =============================================================================

export default {
  ANOMALOUS_BEHAVIORS,
  processAnomalousBehavior,
  processSideEffects,
  showFakeBSOD,
  groundAircraft,
  isAircraftGrounded,
  getGroundedDaysRemaining,
  clearGroundedAircraft,
  updateZeroDividedByZeroPositions,
  startZombieOutbreak,
  updateZombieNews,
  hasAnomalousBehavior,
  getAnomalousBehavior,
  getAllAnomalousAircraftIds
};
